import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ScrollableModal } from '../../components/ui/ScrollableModal';
import { Settings, Loader2 } from 'lucide-react';
import { projectsAPI } from '../../services/api';
import toast from '../../services/toast';
import Input from '../../components/ui/Input';
import FormField from '../../components/forms/FormField';

const emptySettings = { discordWebhookUrl: '' };

export default function ProjectSettingsModal({
  isOpen,
  onClose,
  orgId,
  streamId,
  projectId,
  project,
  onSuccess,
}: any) {
  const [fetching, setFetching] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({ defaultValues: { discordWebhookUrl: '' } });

  useEffect(() => {
    if (isOpen && project) {
      setFetching(true);
      const s =
        project?.settings && typeof project.settings === 'object'
          ? project.settings
          : emptySettings;
      reset({ discordWebhookUrl: s.discordWebhookUrl ?? '' });
      setFetching(false);
    } else if (!isOpen) {
      reset({ discordWebhookUrl: '' });
    }
  }, [isOpen, project, reset]);

  const onSubmit = handleSubmit(async ({ discordWebhookUrl }) => {
    if (!orgId || !streamId || !projectId) return;
    try {
      await projectsAPI.update(orgId, streamId, projectId, {
        settings: {
          discordWebhookUrl: String(discordWebhookUrl || '').trim() || undefined,
        },
      });
      onClose();
      if (onSuccess) onSuccess();
      toast.success('Project settings saved');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to save settings');
    }
  });

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <ScrollableModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Project settings"
      icon={<Settings className="w-5 h-5 text-vivid-red" />}
      disableClose={isSubmitting}
      footer={
        fetching ? null : (
          <>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="btn-secondary-mdl disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="project-settings-form"
              disabled={isSubmitting}
              className="btn-primary-mdl inline-flex items-center gap-2 border-2 border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </>
        )
      }
    >
      <form id="project-settings-form" onSubmit={onSubmit} noValidate>
        {fetching ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-vivid-red" />
          </div>
        ) : (
          <FormField id="project-discord-webhook" label="Discord webhook URL" className="mb-4">
            <Input
              type="url"
              id="project-discord-webhook"
              placeholder="https://discord.com/api/webhooks/..."
              disabled={isSubmitting}
              {...register('discordWebhookUrl')}
            />
            <p className="mt-1 text-xs text-slate-400">
              Optional. Notifications for this project can use this webhook.
            </p>
          </FormField>
        )}
      </form>
    </ScrollableModal>
  );
}
